import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Users, ChevronLeft, RefreshCw, Loader2, AlertCircle, Check, Search, X,
} from "lucide-react";
import {
  useProbationerReviewQueueQuery,
  useAssignProbationerReviewerMutation,
  useUnassignProbationerReviewerMutation,
} from "../api/probationerApplicationApi";
import PageGate from "../../../components/PageGate";
import { PERMS } from "../../../utils/perms";

/**
 * Assign ENTRY applications in a diet to the application review committee.
 *
 * The entry-application counterpart of the upgrade review cycle screen. Auto-
 * Assign scatters everything round-robin in one shot; this is the queue an
 * admin works when they want to place particular applications with particular
 * reviewers, and see what is stuck and why.
 */

const ROLE_LABEL: Record<string, string> = {
  chairman: "Chairman",
  secretary: "Secretary",
  member: "Member",
};

const DECISION_STYLE: Record<string, string> = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
  returned: "bg-amber-50 text-amber-700 border-amber-200",
};

function Stat({ label, value, tone }: { label: string; value: any; tone?: string }) {
  return (
    <div className="rounded-2xl border border-gray-100 p-4">
      <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">{label}</div>
      <div className={`mt-1 text-2xl font-black tabular-nums ${tone ?? "text-gray-900"}`}>
        {value ?? 0}
      </div>
    </div>
  );
}

/**
 * Where an application is read from an admin screen.
 *
 * The ADMIN review page, not /probationer-applications/:id — that one is the
 * applicant's own view of their application and is gated to member and
 * probationer roles, so an admin following it lands on a permission wall
 * rather than the review they were trying to open.
 */
const applicationHref = (id: string | number) => `/probationer-applications/admin/${id}`;

function Breakdown({ title, data }: { title: string; data: Record<string, number> }) {
  const rows = Object.entries(data ?? {}).sort((a, b) => b[1] - a[1]);
  if (!rows.length) return null;
  const max = rows[0][1] || 1;

  return (
    <div>
      <h3 className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">{title}</h3>
      <div className="space-y-1.5">
        {rows.map(([label, count]) => (
          <div key={label} className="flex items-center gap-3">
            <div className="w-40 shrink-0 truncate text-xs font-semibold text-gray-600">{label}</div>
            <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
              <div
                className="h-full rounded-full bg-blue-400"
                style={{ width: `${Math.max(4, (count / max) * 100)}%` }}
              />
            </div>
            <div className="w-8 text-right text-xs font-bold tabular-nums text-gray-700">{count}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminProbationerReviewAssignmentPage() {
  const { dietId = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  /* Where an application opened FROM, carried along so its Back button can
     return here rather than to the category list. Named, so the button can
     say where it goes instead of a bare "Back". */
  const cameFrom = {
    from: { pathname: location.pathname + location.search, label: "Review Assignment" },
  };

  /* The whole row opens the application, not just the name. The name link
     stays — it is what makes the row obviously openable, and it is what a
     middle-click or "open in new tab" needs. */
  const openApplication = (id: string | number) =>
    navigate(applicationHref(id), { state: cameFrom });

  const { data, isLoading, isFetching, refetch } = useProbationerReviewQueueQuery(dietId, {
    skip: !dietId,
  });

  const [assign, { isLoading: assigning }] = useAssignProbationerReviewerMutation();
  const [unassign, { isLoading: unassigning }] = useUnassignProbationerReviewerMutation();

  const diet = data?.diet;
  const committee = data?.committee;
  const panel: any[] = useMemo(() => data?.members ?? [], [data]);
  const assigned: any[] = useMemo(() => data?.assigned ?? [], [data]);
  const pool: any[] = useMemo(() => data?.unassigned ?? [], [data]);
  const notReady: any[] = useMemo(() => data?.not_ready ?? [], [data]);
  const analysis = data?.analysis;

  const [picked, setPicked] = useState<string[]>([]);
  /* The panel member whose work is being looked at. The whole row set is
     already on this page — the dialog is a lens over it, not another fetch. */
  const [panelMember, setPanelMember] = useState<any | null>(null);
  const [reviewer, setReviewer] = useState("");
  const [q, setQ] = useState("");
  const [banner, setBanner] = useState<{ ok: boolean; text: string } | null>(null);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return pool;
    return pool.filter((a) =>
      `${a.name ?? ""} ${a.membership_no ?? ""} ${a.category ?? ""} ${a.chapter ?? ""}`
        .toLowerCase()
        .includes(s),
    );
  }, [pool, q]);

  const allPicked = filtered.length > 0 && filtered.every((a) => picked.includes(a.id));

  const run = async (fn: () => Promise<any>, fallback: string) => {
    setBanner(null);
    try {
      const res: any = await fn();
      setBanner({ ok: true, text: res?.message ?? fallback });
      return true;
    } catch (e: any) {
      setBanner({ ok: false, text: e?.data?.message || "That did not work. Please try again." });
      return false;
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto p-6 space-y-4">
        <div className="h-24 bg-gray-100 rounded-2xl animate-pulse" />
        <div className="h-64 bg-gray-100 rounded-2xl animate-pulse" />
      </div>
    );
  }

  return (
    <PageGate perm={PERMS.application_diet.allCategoriesAccess}>
      <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6">
        {/* Header */}
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
          <Link
            to={`/probationer-applications/admin/submissions/diets/${dietId}/categories`}
            className="inline-flex items-center gap-1 text-xs font-bold text-gray-500 hover:text-gray-900"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Diet categories
          </Link>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-black text-gray-900 tracking-tight">
                  Review Assignment
                </h1>
                <p className="text-sm font-semibold text-gray-500">
                  {[diet?.title, committee?.name].filter(Boolean).join(" · ") || "Entry applications"}
                </p>
              </div>
            </div>
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="p-2.5 bg-white border border-gray-100 rounded-xl hover:bg-gray-50 shadow-sm"
            >
              <RefreshCw className={`w-4 h-4 text-gray-500 ${isFetching ? "animate-spin" : ""}`} />
            </button>
          </div>

          {/* Without a committee there is nobody to assign to — say where to fix it. */}
          {!committee ? (
            <div className="mt-4 flex items-start gap-3 rounded-2xl border border-rose-100 bg-rose-50 p-4">
              <AlertCircle className="w-5 h-5 text-rose-500 shrink-0" />
              <div className="text-sm font-semibold text-rose-800">
                {data?.message ?? "No application review committee was found."}{" "}
                <Link to="/executive-sets" className="underline">
                  Go to Executive Sets
                </Link>
              </div>
            </div>
          ) : null}
        </div>

        {banner && (
          <div
            className={`px-4 py-3 rounded-2xl text-sm font-semibold border ${
              banner.ok
                ? "bg-emerald-50 border-emerald-100 text-emerald-800"
                : "bg-rose-50 border-rose-100 text-rose-800"
            }`}
          >
            {banner.text}
          </div>
        )}

        {/* ── Analysis ────────────────────────────────────────────────── */}
        {analysis ? (
          <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
            <h2 className="text-lg font-black text-gray-900">Analysis</h2>
            <p className="text-sm text-gray-500 mb-4">
              Where this diet's entry review stands.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <Stat label="Submitted" value={analysis.applications?.submitted} />
              <Stat label="Assigned" value={analysis.applications?.assigned} />
              <Stat label="Reviewed" value={analysis.applications?.reviewed} tone="text-emerald-600" />
              <Stat
                label="Awaiting decision"
                value={analysis.applications?.awaiting_decision}
                tone={analysis.applications?.awaiting_decision > 0 ? "text-amber-600" : undefined}
              />
              <Stat label="Waiting in pool" value={analysis.applications?.waiting_in_pool} />
              <Stat
                label="Held back"
                value={analysis.applications?.held_back}
                tone={analysis.applications?.held_back > 0 ? "text-rose-600" : undefined}
              />
            </div>

            <div className="mt-6 grid md:grid-cols-2 gap-6">
              <Breakdown title="By decision" data={analysis.by_decision} />
              <Breakdown title="By category" data={analysis.by_category} />
              <Breakdown title="By chapter" data={analysis.by_chapter} />
            </div>

            {analysis.workload?.length ? (
              <div className="mt-6">
                <h3 className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">
                  Reviewer workload in this diet
                </h3>
                <div className="border rounded-2xl overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] font-black uppercase tracking-widest text-gray-400 border-b">
                        <th className="px-4 py-2">Reviewer</th>
                        <th className="px-4 py-2 text-right">Assigned</th>
                        <th className="px-4 py-2 text-right">Reviewed</th>
                        <th className="px-4 py-2 text-right">Pending</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {analysis.workload.map((w: any) => (
                        <tr key={w.name}>
                          <td className="px-4 py-2 font-semibold text-gray-800">
                            {w.name}
                            {w.is_former ? (
                              <span className="ml-2 text-[10px] font-black uppercase tracking-widest text-gray-400">
                                former
                              </span>
                            ) : null}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums">{w.assigned_count}</td>
                          <td className="px-4 py-2 text-right tabular-nums text-emerald-700">
                            {w.decided_count}
                          </td>
                          <td
                            className={`px-4 py-2 text-right tabular-nums font-bold ${
                              w.pending_count > 0 ? "text-amber-600" : "text-gray-400"
                            }`}
                          >
                            {w.pending_count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {/* ── Panel ───────────────────────────────────────────────────── */}
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
          <h2 className="text-lg font-black text-gray-900">Panel</h2>
          <p className="text-sm text-gray-500 mb-4">
            Everyone on the application review committee. The counts are what each is carrying in
            this diet.
          </p>

          {panel.length === 0 ? (
            <p className="text-sm text-gray-400">Nobody is on the committee yet.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {panel.map((m: any) => (
                // A card is the natural thing to click to ask "what is this
                // person carrying?" — a button rather than a div so the
                // keyboard reaches it too.
                <button
                  key={m.member_id}
                  type="button"
                  onClick={() => setPanelMember(m)}
                  title={`See everything assigned to ${m.name} in this diet`}
                  className={`text-left rounded-2xl border p-4 transition hover:border-blue-300 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-100 ${
                    m.is_former ? "border-dashed border-gray-200 bg-gray-50/60" : "border-gray-100"
                  }`}
                >
                  <div className="font-bold text-gray-900 truncate">{m.name}</div>
                  <div className="text-[11px] font-semibold text-gray-400 mt-0.5">
                    {[ROLE_LABEL[m.committee_role] ?? m.committee_role, m.membership_no]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                  {m.is_former ? (
                    <div className="text-[10px] font-black uppercase tracking-widest text-gray-400 mt-1">
                      No longer on the committee
                    </div>
                  ) : null}
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                        m.pending_count > 0
                          ? "bg-amber-50 text-amber-700 border-amber-200"
                          : "bg-gray-50 text-gray-400 border-gray-200"
                      }`}
                    >
                      {m.pending_count ?? 0} pending
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border bg-gray-50 text-gray-500 border-gray-200">
                      {m.assigned_count ?? 0} assigned
                    </span>
                  </div>
                  <div className="mt-2 text-[11px] font-semibold text-blue-600">
                    See their applications →
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Assign from the pool ────────────────────────────────────── */}
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-black text-gray-900">Ready to assign</h2>
              <p className="text-sm text-gray-500">
                Submitted in this diet, with the application fee settled, the chapter endorsement in
                and both referees approved — and nobody reviewing them yet. Assigning one hands it
                to the Membership Committee.
              </p>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, category or chapter…"
                className="pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-100"
              />
            </div>
          </div>

          {pool.length === 0 ? (
            <p className="mt-6 text-sm text-gray-400">
              Nothing is ready to hand out — every eligible application already has a reviewer.
            </p>
          ) : (
            <>
              <div className="mt-4 border rounded-2xl overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] font-black uppercase tracking-widest text-gray-400 border-b">
                      <th className="px-4 py-2 w-10">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={allPicked}
                          onChange={() => setPicked(allPicked ? [] : filtered.map((a) => a.id))}
                        />
                      </th>
                      <th className="px-4 py-2">Applicant</th>
                      <th className="px-4 py-2">Category</th>
                      <th className="px-4 py-2">Chapter</th>
                      <th className="px-4 py-2 text-right">Waiting</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filtered.map((a) => (
                      <tr
                        key={a.id}
                        onClick={() => openApplication(a.id)}
                        title="Open this application"
                        className="hover:bg-gray-50/60 cursor-pointer"
                      >
                        {/* The checkbox is for picking, not opening — the row
                            click must not fire underneath it, or selecting
                            somebody would navigate away from the selection. */}
                        <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={picked.includes(a.id)}
                            onChange={() =>
                              setPicked((p) =>
                                p.includes(a.id) ? p.filter((x) => x !== a.id) : [...p, a.id],
                              )
                            }
                          />
                        </td>
                        <td className="px-4 py-2.5">
                          <Link
                            to={applicationHref(a.id)}
                            state={cameFrom}
                            className="font-semibold text-gray-900 hover:text-blue-600"
                          >
                            {a.name || "—"}
                          </Link>
                          <div className="text-[11px] text-gray-400">
                            {a.membership_no || a.email}
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-gray-600">{a.category || "—"}</td>
                        <td className="px-4 py-2.5 text-gray-500">{a.chapter || "—"}</td>
                        <td
                          className={`px-4 py-2.5 text-right tabular-nums font-bold ${
                            (a.waiting_days ?? 0) > 30 ? "text-rose-600" : "text-gray-500"
                          }`}
                        >
                          {a.waiting_days != null ? `${a.waiting_days}d` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
                <select
                  value={reviewer}
                  onChange={(e) => setReviewer(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white outline-none focus:ring-2 focus:ring-gray-100"
                >
                  <option value="">Assign to…</option>
                  {/* Former members keep their existing work but take no new. */}
                  {panel
                    .filter((m: any) => !m.is_former)
                    .map((m: any) => (
                      <option key={m.member_id} value={m.member_id}>
                        {m.name} ({m.pending_count ?? 0} pending)
                      </option>
                    ))}
                </select>
                <button
                  onClick={async () => {
                    const done = await run(
                      () => assign({ dietId, member_id: reviewer, application_ids: picked }).unwrap(),
                      "Assigned.",
                    );
                    if (done) setPicked([]);
                  }}
                  disabled={assigning || !reviewer || picked.length === 0}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 disabled:opacity-50"
                >
                  {assigning ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Check className="w-3.5 h-3.5" />
                  )}
                  Assign {picked.length || ""}
                </button>
              </div>
            </>
          )}
        </div>

        {/* ── Already assigned ────────────────────────────────────────── */}
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
          <h2 className="text-lg font-black text-gray-900">Assigned applications</h2>
          <p className="text-sm text-gray-500 mb-4">
            Who is reviewing what, and what they have decided so far.
          </p>

          {assigned.length === 0 ? (
            <p className="text-sm text-gray-400">Nothing has been assigned yet.</p>
          ) : (
            <div className="border rounded-2xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-black uppercase tracking-widest text-gray-400 border-b">
                    <th className="px-4 py-2">Applicant</th>
                    <th className="px-4 py-2">Category</th>
                    <th className="px-4 py-2">Reviewer</th>
                    <th className="px-4 py-2">Decision</th>
                    <th className="px-4 py-2 text-right">Waiting</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {assigned.map((a) => (
                    <tr
                      key={a.id}
                      onClick={() => openApplication(a.id)}
                      title="Open this application"
                      className="hover:bg-gray-50/60 cursor-pointer"
                    >
                      <td className="px-4 py-2.5">
                        <Link
                          to={applicationHref(a.id)}
                          state={cameFrom}
                          className="font-semibold text-gray-900 hover:text-blue-600"
                        >
                          {a.name || "—"}
                        </Link>
                        <div className="text-[11px] text-gray-400">{a.membership_no || a.email}</div>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">{a.category || "—"}</td>
                      <td className="px-4 py-2.5 text-gray-700 font-semibold">
                        {a.reviewer || "—"}
                      </td>
                      <td className="px-4 py-2.5">
                        {a.decision ? (
                          <span
                            className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
                              DECISION_STYLE[a.decision] ??
                              "bg-gray-50 text-gray-600 border-gray-200"
                            }`}
                          >
                            {a.decision}
                          </span>
                        ) : (
                          <span className="text-[11px] font-semibold text-amber-600">pending</span>
                        )}
                      </td>
                      <td
                        className={`px-4 py-2.5 text-right tabular-nums font-bold ${
                          (a.waiting_days ?? 0) > 30 ? "text-rose-600" : "text-gray-500"
                        }`}
                      >
                        {a.waiting_days != null ? `${a.waiting_days}d` : "—"}
                      </td>
                      {/* Withdrawing a reviewer is not "open the row" — kept
                          out of the row's click so a misfire cannot navigate
                          instead of removing, or remove instead of navigating. */}
                      <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                        {/* A recorded decision is part of the record — only an
                            untouched assignment can be withdrawn. */}
                        {!a.decision ? (
                          <button
                            onClick={() =>
                              run(
                                () => unassign({ dietId, applicationId: a.id }).unwrap(),
                                "Reviewer removed.",
                              )
                            }
                            disabled={unassigning}
                            title="Remove this reviewer"
                            className="p-1.5 rounded-lg border border-gray-100 text-gray-400 hover:text-rose-600 hover:border-rose-200 disabled:opacity-50"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Held back ───────────────────────────────────────────────── */}
        {notReady.length > 0 ? (
          <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
            <h2 className="text-lg font-black text-gray-900">Held back</h2>
            <p className="text-sm text-gray-500 mb-4">
              Submitted in this diet but not yet assignable. Each one names what is missing —
              clearing it moves the application into the pool above.
            </p>

            <div className="border rounded-2xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-black uppercase tracking-widest text-gray-400 border-b">
                    <th className="px-4 py-2">Applicant</th>
                    <th className="px-4 py-2">Category</th>
                    <th className="px-4 py-2">Waiting on</th>
                    <th className="px-4 py-2 text-right">Waiting</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {notReady.map((a) => (
                    <tr
                      key={a.id}
                      onClick={() => openApplication(a.id)}
                      title="Open this application"
                      className="hover:bg-gray-50/60 cursor-pointer"
                    >
                      <td className="px-4 py-2.5">
                        <Link
                          to={applicationHref(a.id)}
                          state={cameFrom}
                          className="font-semibold text-gray-900 hover:text-blue-600"
                        >
                          {a.name || "—"}
                        </Link>
                        <div className="text-[11px] text-gray-400">{a.membership_no || a.email}</div>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">{a.category || "—"}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex flex-wrap gap-1.5">
                          {(a.blockers ?? []).map((b: string) => (
                            <span
                              key={b}
                              className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-rose-50 text-rose-700 border-rose-200"
                            >
                              {b}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td
                        className={`px-4 py-2.5 text-right tabular-nums font-bold ${
                          (a.waiting_days ?? 0) > 30 ? "text-rose-600" : "text-gray-500"
                        }`}
                      >
                        {a.waiting_days != null ? `${a.waiting_days}d` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {panelMember ? (
          <PanelMemberDialog
            member={panelMember}
            rows={assigned.filter(
              (a) => String(a.reviewer_id ?? "") === String(panelMember.member_id),
            )}
            linkState={cameFrom}
            onOpen={(id) => {
              // Close first: leaving a dialog mounted over the page you just
              // navigated to is how a back-button press lands somewhere
              // nobody asked for.
              setPanelMember(null);
              openApplication(id);
            }}
            onClose={() => setPanelMember(null)}
          />
        ) : null}
      </div>
    </PageGate>
  );
}

/**
 * Everything one panel member is carrying in this diet.
 *
 * Split into what they have ASSESSED and what is still WAITING on them,
 * because those are two different conversations: the first is a record of
 * work done, the second is the chase list. A single mixed table makes the
 * admin do that sorting by eye every time they open it.
 *
 * Reads the rows the page already has rather than fetching again — the
 * assignment table on the page and this dialog are then incapable of
 * disagreeing about who holds what.
 */
function PanelMemberDialog({
  member,
  rows,
  onOpen,
  linkState,
  onClose,
}: {
  member: any;
  rows: any[];
  onOpen: (id: string | number) => void;
  linkState: any;
  onClose: () => void;
}) {
  const assessed = rows.filter((r) => !!r.decision);
  const awaiting = rows.filter((r) => !r.decision);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-gray-900/60"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b">
          <div className="min-w-0">
            <h3 className="font-bold text-gray-900 truncate">{member.name}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {[ROLE_LABEL[member.committee_role] ?? member.committee_role, member.membership_no]
                .filter(Boolean)
                .join(" · ")}
              {" · "}
              {rows.length} in this diet
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-6">
          {rows.length === 0 ? (
            <p className="text-sm text-gray-500">
              Nothing has been assigned to {member.name} in this diet yet.
            </p>
          ) : (
            <>
              <MemberRows
                title={`Awaiting their assessment (${awaiting.length})`}
                rows={awaiting}
                empty="They have assessed everything handed to them."
                onOpen={onOpen}

                linkState={linkState}
              />
              <MemberRows
                title={`Assessed (${assessed.length})`}
                rows={assessed}
                empty="They have not assessed any of these yet."
                onOpen={onOpen}

                linkState={linkState}
              />
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function MemberRows({
  title,
  rows,
  empty,
  onOpen,
  linkState,
}: {
  title: string;
  rows: any[];
  empty: string;
  onOpen: (id: string | number) => void;
  /** Carried onto the link so the application's Back button returns here. */
  linkState: any;
}) {
  return (
    <div>
      <div className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">
        {title}
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-gray-400">{empty}</p>
      ) : (
        <div className="border rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-[10px] font-black uppercase tracking-widest text-gray-400 border-b">
                <th className="px-3 py-2">Applicant</th>
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2">Decision</th>
                <th className="px-3 py-2 text-right">Waiting</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map((a) => (
                <tr
                  key={a.id}
                  onClick={() => onOpen(a.id)}
                  title="Open this application"
                  className="hover:bg-gray-50/60 cursor-pointer"
                >
                  <td className="px-3 py-2">
                    <Link
                      to={applicationHref(a.id)}
                      state={linkState}
                      className="font-semibold text-gray-900 hover:text-blue-600"
                    >
                      {a.name || "—"}
                    </Link>
                    <div className="text-[11px] text-gray-400">{a.membership_no || a.email}</div>
                  </td>
                  <td className="px-3 py-2 text-gray-600">{a.category || "—"}</td>
                  <td className="px-3 py-2">
                    {a.decision ? (
                      <>
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
                            DECISION_STYLE[a.decision] ?? "bg-gray-50 text-gray-600 border-gray-200"
                          }`}
                        >
                          {a.decision}
                        </span>
                        {/* When, not just what — the date is how an admin
                            tells a decision made this sitting from one
                            carried in from an earlier one. */}
                        {a.decided_at ? (
                          <div className="text-[11px] text-gray-400 mt-0.5">
                            {String(a.decided_at).slice(0, 10)}
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <span className="text-[11px] font-semibold text-amber-600">pending</span>
                    )}
                  </td>
                  <td
                    className={`px-3 py-2 text-right tabular-nums font-bold ${
                      (a.waiting_days ?? 0) > 30 ? "text-rose-600" : "text-gray-500"
                    }`}
                  >
                    {a.waiting_days != null ? `${a.waiting_days}d` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
